import { useState, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, TextField, InputAdornment, Select, MenuItem,
  FormControl, InputLabel, Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Alert, Grid, Divider, List, ListItem, ListItemText
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Article as ArticleIcon,
  Lightbulb as LightbulbIcon
} from '@mui/icons-material';
import * as articleApi from '../../services/article';
import { Article, Innovation } from '../../services/article';

const ContentManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [contentType, setContentType] = useState<'article' | 'innovation'>('article');
  const [articles, setArticles] = useState<Article[]>([]);
  const [innovations, setInnovations] = useState<Innovation[]>([]);
  const [filteredItems, setFilteredItems] = useState<(Article | Innovation)[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState<Article | Innovation | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    fetchContent();
  }, []);

  useEffect(() => {
    filterItems();
  }, [articles, innovations, searchTerm, statusFilter, contentType]);

  const fetchContent = async () => {
    try {
      const [articlesData, innovationsData] = await Promise.all([
        articleApi.getAllArticles(),
        articleApi.getAllInnovations()
      ]);
      setArticles(articlesData);
      setInnovations(innovationsData);
    } catch (error) {
      console.error('Error fetching content:', error);
      setAlert({ type: 'error', message: 'Failed to fetch content' });
    }
  };

  const filterItems = () => {
    const items = contentType === 'article' ? articles : innovations;
    let filtered = items;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(term) ||
        item.author.toLowerCase().includes(term) ||
        item.summary.toLowerCase().includes(term)
      );
    }

    setFilteredItems(filtered);
  };

  const handleApprove = async (item: Article | Innovation) => {
    try {
      setLoading(true);
      if (contentType === 'article') {
        await articleApi.approveArticle(item.id);
      } else {
        await articleApi.approveInnovation(item.id);
      }
      setAlert({ type: 'success', message: `${contentType === 'article' ? 'Article' : 'Innovation'} approved successfully` });
      fetchContent();
    } catch (error) {
      console.error('Error approving item:', error);
      setAlert({ type: 'error', message: 'Failed to approve item' });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedItem) return;

    try {
      setLoading(true);
      if (contentType === 'article') {
        await articleApi.rejectArticle(selectedItem.id, rejectReason);
      } else {
        await articleApi.rejectInnovation(selectedItem.id, rejectReason);
      }
      setAlert({ type: 'success', message: `${contentType === 'article' ? 'Article' : 'Innovation'} rejected` });
      setRejectDialogOpen(false);
      setRejectReason('');
      setSelectedItem(null);
      fetchContent();
    } catch (error) {
      console.error('Error rejecting item:', error);
      setAlert({ type: 'error', message: 'Failed to reject item' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item: Article | Innovation) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      setLoading(true);
      if (contentType === 'article') {
        await articleApi.deleteArticle(item.id);
      } else {
        await articleApi.deleteInnovation(item.id);
      }
      setAlert({ type: 'success', message: `${contentType === 'article' ? 'Article' : 'Innovation'} deleted successfully` });
      fetchContent();
    } catch (error) {
      console.error('Error deleting item:', error);
      setAlert({ type: 'error', message: 'Failed to delete item' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const openDetailsDialog = (item: Article | Innovation) => {
    setSelectedItem(item);
    setDetailsDialogOpen(true);
  };

  const openRejectDialog = (item: Article | Innovation) => {
    setSelectedItem(item);
    setRejectDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Content Management
      </Typography>

      {alert && (
        <Alert
          severity={alert.type}
          onClose={() => setAlert(null)}
          sx={{ mb: 3 }}
        >
          {alert.message}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => {
          setActiveTab(newValue);
          setContentType(newValue === 0 ? 'article' : 'innovation');
        }}
        sx={{ mb: 3 }}
      >
        <Tab
          icon={<ArticleIcon />}
          iconPosition="start"
          label={`Articles (${articles.length})`}
        />
        <Tab
          icon={<LightbulbIcon />}
          iconPosition="start"
          label={`Innovations (${innovations.length})`}
        />
      </Tabs>

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <TextField
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Industry</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">
                    No {contentType === 'article' ? 'articles' : 'innovations'} found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.title}</TableCell>
                  <TableCell>{item.author}</TableCell>
                  <TableCell>{item.industry}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.status}
                      color={getStatusColor(item.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{item.date}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => openDetailsDialog(item)}
                      color="primary"
                    >
                      <ViewIcon />
                    </IconButton>
                    {item.status === 'pending' && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleApprove(item)}
                          color="success"
                          disabled={loading}
                        >
                          <CheckIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openRejectDialog(item)}
                          color="error"
                          disabled={loading}
                        >
                          <CloseIcon />
                        </IconButton>
                      </>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(item)}
                      color="error"
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {contentType === 'article' ? 'Article' : 'Innovation'} Details
            </Typography>
            <IconButton onClick={() => setDetailsDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedItem && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h5" gutterBottom>
                  {selectedItem.title}
                </Typography>
                <Chip
                  label={selectedItem.status}
                  color={getStatusColor(selectedItem.status) as any}
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Author
                </Typography>
                <Typography variant="body1">{selectedItem.author}</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Industry
                </Typography>
                <Typography variant="body1">{selectedItem.industry}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Summary
                </Typography>
                <Typography variant="body1">{selectedItem.summary}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Content
                </Typography>
                {selectedItem.content.map((paragraph, index) => (
                  <Typography key={index} variant="body2" paragraph>
                    {paragraph}
                  </Typography>
                ))}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Key Points
                </Typography>
                <List dense>
                  {selectedItem.keyPoints.map((point, index) => (
                    <ListItem key={index}>
                      <CheckIcon color="success" sx={{ mr: 1 }} />
                      <ListItemText primary={point} />
                    </ListItem>
                  ))}
                </List>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Conclusion
                </Typography>
                <Typography variant="body1">{selectedItem.conclusion}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selectedItem.tags.map((tag, index) => (
                    <Chip key={index} label={tag} size="small" />
                  ))}
                </Box>
              </Grid>

              {selectedItem.status === 'rejected' && selectedItem.rejectionReason && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <Typography variant="subtitle2">Rejection Reason:</Typography>
                    <Typography variant="body2">{selectedItem.rejectionReason}</Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedItem?.status === 'pending' && (
            <>
              <Button
                onClick={() => {
                  setDetailsDialogOpen(false);
                  handleApprove(selectedItem);
                }}
                color="success"
                variant="contained"
                disabled={loading}
              >
                Approve
              </Button>
              <Button
                onClick={() => {
                  setDetailsDialogOpen(false);
                  openRejectDialog(selectedItem);
                }}
                color="error"
                variant="outlined"
                disabled={loading}
              >
                Reject
              </Button>
            </>
          )}
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject {contentType === 'article' ? 'Article' : 'Innovation'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            margin="normal"
            placeholder="Provide a reason for rejection..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={loading || !rejectReason.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentManagement;
